// Main Payroll module - left sub-nav with multiple views inside one route.
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams, Outlet } from "react-router-dom";
import { useAuth, hasRole } from "@/lib/auth";
import { Payroll } from "@/lib/payroll";
import { api, fmtINR, fmtDate, fmtDateTime } from "@/lib/api";
import { PageHeader, SectionTitle } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Plus, FileText, Download, Trash, ChartLineUp, Wallet, Receipt, FilePdf, ClipboardText, Gear, ArrowsClockwise, CurrencyInr, GraduationCap, UserMinus, Coins, Buildings } from "@phosphor-icons/react";
import { Field, StatusBadge } from "./Employees";
import { MultiSelect } from "@/components/MultiSelect";

const SUB_NAV = [
  { to: "", label: "Dashboard", icon: ChartLineUp, roles: "*" },
  { to: "structures", label: "Salary Structures", icon: Coins, roles: ["super_admin", "hr"] },
  { to: "compensation", label: "Employee Compensation", icon: Wallet, roles: ["super_admin", "hr"] },
  { to: "processing", label: "Payroll Processing", icon: ArrowsClockwise, roles: ["super_admin", "hr", "finance_manager"] },
  { to: "payslips", label: "Payslips", icon: FilePdf, roles: "*" },
  { to: "declarations", label: "Tax Declarations", icon: GraduationCap, roles: ["super_admin", "hr", "finance_manager"] },
  { to: "revisions", label: "Salary Revisions", icon: ClipboardText, roles: ["super_admin", "hr"] },
  { to: "loans", label: "Loans & Advances", icon: CurrencyInr, roles: "*" },
  { to: "fnf", label: "Full & Final", icon: UserMinus, roles: ["super_admin", "hr"] },
  { to: "reports", label: "Reports", icon: FileText, roles: ["super_admin", "hr", "finance_manager"] },
  { to: "settings", label: "Settings", icon: Gear, roles: ["super_admin", "hr"] },
];

export default function PayrollModule() {
  const { user } = useAuth();
  const params = useParams();
  const navigate = useNavigate();
  const tab = params["*"] || "";
  const visible = SUB_NAV.filter((n) => n.roles === "*" || n.roles.includes(user.role));
  return (
    <div data-testid="payroll-page" className="flex flex-col lg:flex-row gap-6">
      <aside className="lg:w-56 shrink-0">
        <div className="lg:sticky lg:top-20 space-y-0.5">
          {visible.map((n) => {
            const isActive = (n.to === "" && tab === "") || (n.to !== "" && tab === n.to);
            return (
              <button
                key={n.to}
                onClick={() => navigate(n.to ? `/payroll/${n.to}` : "/payroll")}
                data-testid={`payroll-nav-${n.to || "dashboard"}`}
                className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-md text-sm transition-colors text-left
                ${isActive ? "bg-[#fd7e14] text-white" : "hover:bg-[hsl(var(--muted))]"}`}
              >
                <n.icon size={16} weight="duotone" />
                <span>{n.label}</span>
              </button>
            );
          })}
        </div>
      </aside>
      <div className="flex-1 min-w-0">
        {tab === "" && <DashboardView />}
        {tab === "structures" && <StructuresView />}
        {tab === "compensation" && <CompensationView />}
        {tab === "processing" && <ProcessingView />}
        {tab === "payslips" && <PayslipsView />}
        {tab === "declarations" && <DeclarationsView />}
        {tab === "revisions" && <RevisionsView />}
        {tab === "loans" && <LoansView />}
        {tab === "fnf" && <FNFView />}
        {tab === "reports" && <ReportsView />}
        {tab === "settings" && <SettingsView />}
      </div>
    </div>
  );
}

// ============== DASHBOARD ==============
function DashboardView() {
  const [d, setD] = useState(null);
  useEffect(() => { Payroll.dashboard().then(setD); }, []);
  if (!d) return <div className="opacity-60 text-sm">Loading…</div>;
  return (
    <div>
      <PageHeader title="Payroll Dashboard" description="Overview of payroll status, runs and pending workflows." />
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Kpi label="Active Employees" value={d.total_employees} testid="pkpi-employees" />
        <Kpi label="Salary Structures" value={d.structures} testid="pkpi-structures" />
        <Kpi label="Draft Runs" value={d.draft_runs} testid="pkpi-draft" />
        <Kpi label="Pending Review" value={d.pending_review} testid="pkpi-review" accent={d.pending_review > 0 ? "text-amber-600" : ""} />
        <Kpi label="Pending Loans" value={d.pending_loans} testid="pkpi-loans" />
        <Kpi label="Pending Advances" value={d.pending_advances} testid="pkpi-advances" />
        <Kpi label="Last Run Net" value={fmtINR(d.last_run_total || 0)} testid="pkpi-lastnet" accent="text-[#fd7e14]" />
        <Kpi label="Trend Points" value={(d.trend || []).length} testid="pkpi-trend" />
      </div>
      {d.last_run && (
        <Card className="p-5">
          <h3 className="font-semibold mb-2">Last Processed Run</h3>
          <div className="text-sm opacity-70">Month {d.last_run.month}/{d.last_run.year} • {d.last_run.totals?.employees} employees • Net {fmtINR(d.last_run.totals?.net || 0)}</div>
        </Card>
      )}
    </div>
  );
}

const Kpi = ({ label, value, accent = "", testid }) => (
  <Card data-testid={testid} className="p-4">
    <div className="text-xs uppercase tracking-[0.15em] opacity-60">{label}</div>
    <div className={`mt-1 text-2xl font-semibold ${accent}`} style={{ fontFamily: "Outfit, sans-serif" }}>{value}</div>
  </Card>
);

// ============== STRUCTURES ==============
function StructuresView() {
  const [items, setItems] = useState([]);
  const [components, setComponents] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const empty = { name: "", description: "", components: [], active: true };
  const [form, setForm] = useState(empty);

  const load = () => Payroll.listStructures().then(setItems);
  useEffect(() => { load(); Payroll.listComponents().then(setComponents); }, []);

  const addRow = () => setForm({ ...form, components: [...form.components, { component_code: "", value_type: "fixed", value: 0 }] });
  const updRow = (i, k, v) => { const arr = [...form.components]; arr[i] = { ...arr[i], [k]: v }; setForm({ ...form, components: arr }); };
  const rmRow = (i) => { const arr = [...form.components]; arr.splice(i, 1); setForm({ ...form, components: arr }); };

  const save = async () => {
    if (!form.name) return toast.error("Name required");
    try {
      if (editing) await Payroll.updateStructure(editing.id, form);
      else await Payroll.createStructure(form);
      toast.success("Saved"); setOpen(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div>
      <PageHeader title="Salary Structures" description="Reusable templates of earning & deduction components." action={
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button onClick={() => { setEditing(null); setForm(empty); }} className="btn-brand" data-testid="structure-new"><Plus size={16} className="mr-1.5" />New Structure</Button>
          </DialogTrigger>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>{editing ? "Edit" : "New"} Salary Structure</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <Field label="Name *"><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="structure-name" /></Field>
              <Field label="Description"><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></Field>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label>Components</Label>
                  <Button size="sm" variant="outline" onClick={addRow} data-testid="structure-add-comp"><Plus size={14} className="mr-1" />Add</Button>
                </div>
                <div className="space-y-2">
                  {form.components.map((row, i) => (
                    <div key={i} className="grid grid-cols-12 gap-2 items-center">
                      <Select value={row.component_code} onValueChange={(v) => updRow(i, "component_code", v)}>
                        <SelectTrigger className="col-span-5"><SelectValue placeholder="Component" /></SelectTrigger>
                        <SelectContent>{components.map((c) => <SelectItem key={c.code} value={c.code}>{c.name} ({c.type})</SelectItem>)}</SelectContent>
                      </Select>
                      <Select value={row.value_type} onValueChange={(v) => updRow(i, "value_type", v)}>
                        <SelectTrigger className="col-span-4"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="fixed">Fixed Amount</SelectItem>
                          <SelectItem value="percent_of_basic">% of Basic</SelectItem>
                          <SelectItem value="percent_of_ctc">% of CTC</SelectItem>
                        </SelectContent>
                      </Select>
                      <Input className="col-span-2" type="number" value={row.value} onChange={(e) => updRow(i, "value", Number(e.target.value))} />
                      <Button size="icon" variant="ghost" onClick={() => rmRow(i)} className="col-span-1"><Trash size={14} className="text-red-600" /></Button>
                    </div>
                  ))}
                  {form.components.length === 0 && <p className="text-sm opacity-60">No components yet — add at least Basic to get started.</p>}
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button onClick={save} className="btn-brand" data-testid="structure-save">Save</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      } />
      <Card className="p-0 overflow-hidden">
        <Table>
          <TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Components</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Action</TableHead></TableRow></TableHeader>
          <TableBody>
            {items.map((s) => (
              <TableRow key={s.id}>
                <TableCell><div className="font-medium">{s.name}</div><div className="text-xs opacity-60">{s.description}</div></TableCell>
                <TableCell className="text-xs">{(s.components || []).map(c => c.component_code).join(", ")}</TableCell>
                <TableCell>{s.active ? <Badge>Active</Badge> : <Badge variant="outline">Inactive</Badge>}</TableCell>
                <TableCell className="text-right"><Button size="sm" variant="outline" onClick={() => { setEditing(s); setForm({ ...empty, ...s }); setOpen(true); }}>Edit</Button></TableCell>
              </TableRow>
            ))}
            {items.length === 0 && <TableRow><TableCell colSpan={4} className="text-center text-sm opacity-60 py-10">No structures yet.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}

// ============== EMPLOYEE COMPENSATION ==============
function CompensationView() {
  const [employees, setEmployees] = useState([]);
  const [structures, setStructures] = useState([]);
  const [comps, setComps] = useState([]);
  const [open, setOpen] = useState(false);
  const empty = { employee_id: "", structure_id: "", ctc_annual: 0, effective_date: new Date().toISOString().slice(0, 10), overrides: {} };
  const [form, setForm] = useState(empty);

  const load = () => Payroll.listCompensations().then(setComps);
  useEffect(() => {
    load();
    api.get("/employees/lookup/all").then((r) => setEmployees(r.data));
    Payroll.listStructures().then(setStructures);
  }, []);

  const save = async () => {
    if (!form.employee_id || !form.structure_id) return toast.error("Employee and structure required");
    try { await Payroll.saveCompensation({ ...form, ctc_annual: Number(form.ctc_annual) }); toast.success("Saved"); setOpen(false); setForm(empty); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div>
      <PageHeader title="Employee Compensation" description="Assign salary structure & CTC. Older entries kept as revision history." action={
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button className="btn-brand" data-testid="compensation-new" onClick={() => setForm(empty)}><Plus size={16} className="mr-1.5" />Assign</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Assign Compensation</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <Field label="Employee">
                <Select value={form.employee_id} onValueChange={(v) => setForm({ ...form, employee_id: v })}>
                  <SelectTrigger data-testid="comp-employee"><SelectValue placeholder="Pick employee" /></SelectTrigger>
                  <SelectContent>{employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.full_name}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label="Salary Structure">
                <Select value={form.structure_id} onValueChange={(v) => setForm({ ...form, structure_id: v })}>
                  <SelectTrigger data-testid="comp-structure"><SelectValue placeholder="Pick structure" /></SelectTrigger>
                  <SelectContent>{structures.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label="CTC (Annual ₹)"><Input type="number" value={form.ctc_annual} data-testid="comp-ctc" onChange={(e) => setForm({ ...form, ctc_annual: e.target.value })} /></Field>
              <Field label="Effective From"><Input type="date" value={form.effective_date} onChange={(e) => setForm({ ...form, effective_date: e.target.value })} /></Field>
            </div>
            <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={save} className="btn-brand" data-testid="comp-save">Save</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      } />
      <Card className="p-0 overflow-hidden">
        <Table>
          <TableHeader><TableRow><TableHead>Employee</TableHead><TableHead>Structure</TableHead><TableHead>CTC</TableHead><TableHead>Effective</TableHead></TableRow></TableHeader>
          <TableBody>
            {comps.map((c) => (
              <TableRow key={c.id}>
                <TableCell className="font-medium">{employees.find((e) => e.id === c.employee_id)?.full_name || c.employee_id}</TableCell>
                <TableCell>{structures.find((s) => s.id === c.structure_id)?.name || "—"}</TableCell>
                <TableCell className="font-semibold">{fmtINR(c.ctc_annual)}</TableCell>
                <TableCell>{fmtDate(c.effective_date)}</TableCell>
              </TableRow>
            ))}
            {comps.length === 0 && <TableRow><TableCell colSpan={4} className="text-center py-10 text-sm opacity-60">No compensations yet.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}

// ============== PROCESSING ==============
function ProcessingView() {
  const { user } = useAuth();
  const [runs, setRuns] = useState([]);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(null);
  const today = new Date();
  const [form, setForm] = useState({ month: today.getMonth() + 1, year: today.getFullYear(), notes: "" });

  const load = () => Payroll.listRuns().then(setRuns);
  useEffect(() => { load(); }, []);

  const create = async () => {
    try { await Payroll.createRun({ ...form, month: Number(form.month), year: Number(form.year) }); toast.success("Run created"); setOpen(false); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const compute = async (id) => {
    if (!window.confirm("Compute payroll for all active employees with active compensation?")) return;
    try { const r = await Payroll.computeRun(id); toast.success(`Computed ${r.employees} employees`); load(); setActive(await Payroll.getRun(id)); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const action = async (id, act) => {
    const comment = window.prompt(`Add a comment for "${act}"`, "");
    if (comment === null) return;
    try { await Payroll.runAction(id, { action: act, comment }); toast.success("Done"); load(); if (active?.id === id) setActive(await Payroll.getRun(id)); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div>
      <PageHeader title="Payroll Processing" description="Create a monthly run, compute, review and approve." action={
        hasRole(user, "super_admin", "hr") && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button className="btn-brand" data-testid="run-new"><Plus size={16} className="mr-1.5" />New Run</Button></DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Create Payroll Run</DialogTitle></DialogHeader>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Month"><Input type="number" min="1" max="12" value={form.month} onChange={(e) => setForm({ ...form, month: e.target.value })} data-testid="run-month" /></Field>
                <Field label="Year"><Input type="number" value={form.year} onChange={(e) => setForm({ ...form, year: e.target.value })} data-testid="run-year" /></Field>
                <div className="col-span-2"><Field label="Notes"><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></Field></div>
              </div>
              <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={create} className="btn-brand" data-testid="run-create">Create</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        )
      } />

      <Card className="p-0 overflow-hidden mb-4">
        <Table>
          <TableHeader><TableRow><TableHead>Period</TableHead><TableHead>Status</TableHead><TableHead>Employees</TableHead><TableHead>Net</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
          <TableBody>
            {runs.map((r) => (
              <TableRow key={r.id} data-testid={`run-row-${r.id}`}>
                <TableCell>{String(r.month).padStart(2, "0")} / {r.year}</TableCell>
                <TableCell><Badge variant={r.status === "processed" ? "default" : "outline"} className="capitalize">{r.status.replace(/_/g, " ")}</Badge></TableCell>
                <TableCell>{r.totals?.employees ?? "—"}</TableCell>
                <TableCell className="font-semibold">{fmtINR(r.totals?.net || 0)}</TableCell>
                <TableCell className="text-right space-x-1.5">
                  <Button size="sm" variant="outline" onClick={async () => setActive(await Payroll.getRun(r.id))}>Open</Button>
                  {hasRole(user, "super_admin", "hr") && (r.status === "draft" || r.status === "under_review") && (
                    <Button size="sm" variant="outline" onClick={() => compute(r.id)} data-testid={`run-compute-${r.id}`}>Compute</Button>
                  )}
                  {hasRole(user, "super_admin", "finance_manager") && r.status === "under_review" && (
                    <Button size="sm" className="btn-brand" onClick={() => action(r.id, "approve")} data-testid={`run-approve-${r.id}`}>Approve</Button>
                  )}
                  {hasRole(user, "super_admin") && r.status === "approved" && (
                    <Button size="sm" className="btn-brand" onClick={() => action(r.id, "finalize")} data-testid={`run-finalize-${r.id}`}>Finalize</Button>
                  )}
                  {hasRole(user, "super_admin", "hr", "finance_manager") && r.status === "under_review" && (
                    <Button size="sm" variant="outline" className="text-red-600" onClick={() => action(r.id, "reject")}>Reject</Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {runs.length === 0 && <TableRow><TableCell colSpan={5} className="text-center py-10 text-sm opacity-60">No runs yet.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </Card>

      {active && (
        <Card className="p-5">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold">Run {String(active.month).padStart(2, "0")}/{active.year}</h3>
            <Button size="sm" variant="outline" onClick={() => setActive(null)}>Close</Button>
          </div>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader><TableRow><TableHead>Employee</TableHead><TableHead>Gross</TableHead><TableHead>Deductions</TableHead><TableHead>Net</TableHead></TableRow></TableHeader>
              <TableBody>
                {(active.items || []).map((it) => (
                  <TableRow key={it.id}>
                    <TableCell>{it.employee_name}<div className="text-xs opacity-60">{it.employee_code}</div></TableCell>
                    <TableCell>{fmtINR(it.earning_total)}</TableCell>
                    <TableCell>{fmtINR(it.deduction_total)}</TableCell>
                    <TableCell className="font-semibold">{fmtINR(it.net_salary)}</TableCell>
                  </TableRow>
                ))}
                {(active.items || []).length === 0 && <TableRow><TableCell colSpan={4} className="text-center py-6 text-sm opacity-60">Not computed yet.</TableCell></TableRow>}
              </TableBody>
            </Table>
          </div>
        </Card>
      )}
    </div>
  );
}

// ============== PAYSLIPS ==============
function PayslipsView() {
  const { user } = useAuth();
  const isAdmin = hasRole(user, "super_admin", "hr");
  const [items, setItems] = useState([]);
  useEffect(() => { Payroll.listPayslips(isAdmin ? undefined : user.id).then(setItems); }, [user.id, isAdmin]);
  return (
    <div>
      <PageHeader title="Payslips" description="Download monthly payslip PDFs." />
      <Card className="p-0 overflow-hidden">
        <Table>
          <TableHeader><TableRow><TableHead>Month</TableHead><TableHead>Year</TableHead>{isAdmin && <TableHead>Employee</TableHead>}<TableHead>Generated</TableHead><TableHead className="text-right">Action</TableHead></TableRow></TableHeader>
          <TableBody>
            {items.map((p) => (
              <TableRow key={p.id}>
                <TableCell>{String(p.month).padStart(2, "0")}</TableCell>
                <TableCell>{p.year}</TableCell>
                {isAdmin && <TableCell className="text-xs font-mono opacity-70">{p.employee_id?.slice(0, 8)}…</TableCell>}
                <TableCell className="text-xs opacity-60">{fmtDateTime(p.generated_at)}</TableCell>
                <TableCell className="text-right">
                  <a href={Payroll.payslipPdfUrl(p.id) + `&_t=${Date.now()}`.replace("&", "?")} target="_blank" rel="noreferrer">
                    <Button size="sm" variant="outline" data-testid={`payslip-${p.id}`}><Download size={14} className="mr-1" />PDF</Button>
                  </a>
                </TableCell>
              </TableRow>
            ))}
            {items.length === 0 && <TableRow><TableCell colSpan={isAdmin ? 5 : 4} className="text-center py-10 text-sm opacity-60">No payslips yet.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}

// ============== DECLARATIONS ==============
const SECTIONS = [
  { value: "80C", label: "Section 80C", sub: ["EPF", "PPF", "ELSS", "LIC", "Tax_FD"] },
  { value: "80D", label: "Section 80D - Medical Insurance", sub: ["MED_INS"] },
  { value: "NPS", label: "NPS", sub: ["NPS"] },
  { value: "HOME_LOAN", label: "Home Loan", sub: ["INTEREST", "PRINCIPAL"] },
  { value: "EDU_LOAN", label: "Education Loan", sub: ["INTEREST"] },
  { value: "OTHER", label: "Other Eligible", sub: ["OTHER"] },
];

function DeclarationsView() {
  const { user } = useAuth();
  const isAdmin = hasRole(user, "super_admin", "hr");
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const FY = "2025-26";
  const empty = { financial_year: FY, regime: "old", items: [], status: "draft" };
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);

  const load = () => Payroll.listDeclarations().then(setItems);
  useEffect(() => { load(); }, []);

  const addItem = () => setForm({ ...form, items: [...form.items, { section: "80C", sub_category: "EPF", amount: 0, proof_file_id: null }] });
  const updItem = (i, k, v) => { const arr = [...form.items]; arr[i] = { ...arr[i], [k]: v }; setForm({ ...form, items: arr }); };
  const rmItem = (i) => { const arr = [...form.items]; arr.splice(i, 1); setForm({ ...form, items: arr }); };
  const uploadProof = async (i, file) => {
    if (!file) return;
    const fd = new FormData(); fd.append("file", file); fd.append("category", "tax_proof");
    const r = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
    updItem(i, "proof_file_id", r.data.id);
    toast.success("Proof attached");
  };
  const save = async (status) => {
    const payload = { ...form, status: status || form.status };
    try {
      if (editing) await Payroll.updateDeclaration(editing.id, payload);
      else await Payroll.createDeclaration(payload);
      toast.success("Saved"); setOpen(false); setForm(empty); setEditing(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const review = async (d, decision) => {
    const c = window.prompt(`Comment for ${decision}`, "OK") || "";
    try { await Payroll.reviewDeclaration(d.id, { decision, comment: c }); toast.success("Reviewed"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div>
      <PageHeader title="Tax / Investment Declarations" description="Declare investments to reduce TDS (Old regime)." action={
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button className="btn-brand" data-testid="decl-new" onClick={() => { setEditing(null); setForm(empty); }}><Plus size={16} className="mr-1.5" />New Declaration</Button></DialogTrigger>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>{editing ? "Edit" : "New"} Declaration</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-3 mb-3">
              <Field label="Financial Year"><Input value={form.financial_year} onChange={(e) => setForm({ ...form, financial_year: e.target.value })} /></Field>
              <Field label="Regime">
                <Select value={form.regime} onValueChange={(v) => setForm({ ...form, regime: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="old">Old</SelectItem><SelectItem value="new">New</SelectItem></SelectContent>
                </Select>
              </Field>
            </div>
            <div className="flex items-center justify-between mb-2">
              <Label>Items</Label>
              <Button size="sm" variant="outline" onClick={addItem}><Plus size={14} className="mr-1" />Add</Button>
            </div>
            <div className="space-y-2">
              {form.items.map((it, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 items-center">
                  <Select value={it.section} onValueChange={(v) => updItem(i, "section", v)}>
                    <SelectTrigger className="col-span-3"><SelectValue /></SelectTrigger>
                    <SelectContent>{SECTIONS.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
                  </Select>
                  <Select value={it.sub_category} onValueChange={(v) => updItem(i, "sub_category", v)}>
                    <SelectTrigger className="col-span-3"><SelectValue /></SelectTrigger>
                    <SelectContent>{(SECTIONS.find((s) => s.value === it.section)?.sub || []).map((sc) => <SelectItem key={sc} value={sc}>{sc}</SelectItem>)}</SelectContent>
                  </Select>
                  <Input className="col-span-2" type="number" value={it.amount} onChange={(e) => updItem(i, "amount", Number(e.target.value))} />
                  <Input className="col-span-3" type="file" onChange={(e) => uploadProof(i, e.target.files?.[0])} />
                  <Button size="icon" variant="ghost" onClick={() => rmItem(i)} className="col-span-1"><Trash size={14} className="text-red-600" /></Button>
                </div>
              ))}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button variant="outline" onClick={() => save("draft")} data-testid="decl-save-draft">Save Draft</Button>
              <Button onClick={() => save("submitted")} className="btn-brand" data-testid="decl-submit">Submit for Review</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      } />
      <Card className="p-0 overflow-hidden">
        <Table>
          <TableHeader><TableRow><TableHead>FY</TableHead><TableHead>Regime</TableHead><TableHead>Items</TableHead><TableHead>Total</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
          <TableBody>
            {items.map((d) => {
              const total = (d.items || []).reduce((s, x) => s + (x.amount || 0), 0);
              return (
                <TableRow key={d.id}>
                  <TableCell>{d.financial_year}</TableCell>
                  <TableCell className="capitalize">{d.regime}</TableCell>
                  <TableCell>{d.items?.length || 0}</TableCell>
                  <TableCell className="font-semibold">{fmtINR(total)}</TableCell>
                  <TableCell><Badge variant="outline" className="capitalize">{d.status}</Badge></TableCell>
                  <TableCell className="text-right space-x-1.5">
                    {d.status === "draft" && <Button size="sm" variant="outline" onClick={() => { setEditing(d); setForm({ ...empty, ...d }); setOpen(true); }}>Edit</Button>}
                    {isAdmin && d.status === "submitted" && <>
                      <Button size="sm" className="btn-brand" onClick={() => review(d, "approved")}>Approve</Button>
                      <Button size="sm" variant="outline" className="text-red-600" onClick={() => review(d, "rejected")}>Reject</Button>
                    </>}
                  </TableCell>
                </TableRow>
              );
            })}
            {items.length === 0 && <TableRow><TableCell colSpan={6} className="text-center py-10 text-sm opacity-60">No declarations yet.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}

// ============== REVISIONS ==============
function RevisionsView() {
  const [items, setItems] = useState([]);
  const [employees, setEmployees] = useState([]);
  useEffect(() => {
    Payroll.listRevisions().then(setItems);
    api.get("/employees/lookup/all").then((r) => setEmployees(r.data));
  }, []);
  return (
    <div>
      <PageHeader title="Salary Revisions" description="Automatic history of compensation changes." />
      <Card className="p-0 overflow-hidden">
        <Table>
          <TableHeader><TableRow><TableHead>Employee</TableHead><TableHead>From</TableHead><TableHead>To</TableHead><TableHead>Effective</TableHead><TableHead>Reason</TableHead></TableRow></TableHeader>
          <TableBody>
            {items.map((r) => (
              <TableRow key={r.id}>
                <TableCell className="font-medium">{employees.find((e) => e.id === r.employee_id)?.full_name || r.employee_id}</TableCell>
                <TableCell>{fmtINR(r.from_ctc)}</TableCell>
                <TableCell className="font-semibold">{fmtINR(r.to_ctc)}</TableCell>
                <TableCell>{fmtDate(r.effective_date)}</TableCell>
                <TableCell className="text-sm opacity-70">{r.reason}</TableCell>
              </TableRow>
            ))}
            {items.length === 0 && <TableRow><TableCell colSpan={5} className="text-center py-10 text-sm opacity-60">No revisions yet.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}

// ============== LOANS & ADVANCES ==============
function LoansView() {
  const { user } = useAuth();
  const canApply = user.role !== "super_admin";
  const [loans, setLoans] = useState([]);
  const [advs, setAdvs] = useState([]);
  const [open, setOpen] = useState(false);
  const [openAdv, setOpenAdv] = useState(false);
  const [form, setForm] = useState({ loan_type: "personal", amount: 0, installments: 12, monthly_installment: 0, interest_pct: 0, reason: "" });
  const [advForm, setAdvForm] = useState({ amount: 0, monthly_installment: 0, reason: "" });

  const load = () => { Payroll.listLoans().then(setLoans); Payroll.listAdvances().then(setAdvs); };
  useEffect(() => { load(); }, []);
  const applyLoan = async () => {
    try { await Payroll.createLoan({ ...form, amount: Number(form.amount), installments: Number(form.installments), monthly_installment: Number(form.monthly_installment) || 0, interest_pct: Number(form.interest_pct) || 0 }); toast.success("Loan request submitted"); setOpen(false); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const applyAdv = async () => {
    try { await Payroll.createAdvance({ ...advForm, amount: Number(advForm.amount), monthly_installment: Number(advForm.monthly_installment) || 0 }); toast.success("Advance request submitted"); setOpenAdv(false); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const actLoan = async (l, act) => {
    const c = window.prompt(`Comment for ${act}`, ""); if (c === null) return;
    try { await Payroll.loanAction(l.id, { action: act, comment: c || act }); toast.success("Done"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const actAdv = async (a, act) => {
    const c = window.prompt(`Comment for ${act}`, ""); if (c === null) return;
    try { await Payroll.advanceAction(a.id, { action: act, comment: c || act }); toast.success("Done"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div>
      <PageHeader title="Loans & Advances" description="Employees apply, managers/Finance Manager/SA approve. EMIs auto-deducted in payroll." />
      <Tabs defaultValue="loans">
        <TabsList>
          <TabsTrigger value="loans" data-testid="loans-tab">Loans</TabsTrigger>
          <TabsTrigger value="advances" data-testid="advances-tab">Salary Advances</TabsTrigger>
        </TabsList>
        <TabsContent value="loans" className="mt-4 space-y-3">
          {canApply && (
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild><Button className="btn-brand" data-testid="loan-new"><Plus size={16} className="mr-1.5" />Apply for Loan</Button></DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>New Loan Request</DialogTitle></DialogHeader>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Loan Type">
                    <Select value={form.loan_type} onValueChange={(v) => setForm({ ...form, loan_type: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>{["personal","education","medical","festival","other"].map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                    </Select>
                  </Field>
                  <Field label="Amount (₹)"><Input type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="loan-amount" /></Field>
                  <Field label="No. of Installments"><Input type="number" value={form.installments} onChange={(e) => setForm({ ...form, installments: e.target.value })} /></Field>
                  <Field label="Monthly Deduction (₹)"><Input type="number" value={form.monthly_installment} onChange={(e) => setForm({ ...form, monthly_installment: e.target.value })} data-testid="loan-monthly" placeholder="How much to deduct per month from salary" /></Field>
                  <Field label="Interest % (annual)"><Input type="number" step="0.1" value={form.interest_pct} onChange={(e) => setForm({ ...form, interest_pct: e.target.value })} placeholder="0 (no interest)" /></Field>
                  <div className="col-span-2"><Field label="Reason"><Textarea value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} /></Field></div>
                </div>
                <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button className="btn-brand" onClick={applyLoan} data-testid="loan-submit">Submit</Button></DialogFooter>
              </DialogContent>
            </Dialog>
          )}
          <Card className="p-0 overflow-hidden">
            <Table>
              <TableHeader><TableRow><TableHead>Applicant</TableHead><TableHead>Type</TableHead><TableHead>Amount</TableHead><TableHead>EMI</TableHead><TableHead>Paid</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
              <TableBody>
                {loans.map((l) => (
                  <TableRow key={l.id}>
                    <TableCell>{l.applicant_name}</TableCell>
                    <TableCell className="capitalize">{l.loan_type}</TableCell>
                    <TableCell className="font-semibold">{fmtINR(l.amount)}</TableCell>
                    <TableCell>{fmtINR(l.emi)}</TableCell>
                    <TableCell>{l.paid_installments || 0}/{l.installments}</TableCell>
                    <TableCell><Badge variant="outline" className="capitalize">{l.status}</Badge></TableCell>
                    <TableCell className="text-right space-x-1">
                      {l.status === "pending" && (
                        (user.role === l.chain?.[l.current_step] || (l.chain?.[l.current_step] === "department_manager" && user.is_department_manager)) && <>
                          <Button size="sm" className="btn-brand" onClick={() => actLoan(l, "approve")} data-testid={`loan-approve-${l.id}`}>Approve</Button>
                          <Button size="sm" variant="outline" className="text-red-600" onClick={() => actLoan(l, "reject")}>Reject</Button>
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                {loans.length === 0 && <TableRow><TableCell colSpan={7} className="text-center py-10 text-sm opacity-60">No loans.</TableCell></TableRow>}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>
        <TabsContent value="advances" className="mt-4 space-y-3">
          {canApply && (
            <Dialog open={openAdv} onOpenChange={setOpenAdv}>
              <DialogTrigger asChild><Button className="btn-brand" data-testid="adv-new"><Plus size={16} className="mr-1.5" />Request Advance</Button></DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>Salary Advance Request</DialogTitle></DialogHeader>
                <div className="space-y-3">
                  <Field label="Amount (₹)"><Input type="number" value={advForm.amount} onChange={(e) => setAdvForm({ ...advForm, amount: e.target.value })} data-testid="adv-amount" /></Field>
                  <Field label="Monthly Deduction (₹)"><Input type="number" value={advForm.monthly_installment} onChange={(e) => setAdvForm({ ...advForm, monthly_installment: e.target.value })} placeholder="Optional — defaults to recover next month" /></Field>
                  <Field label="Reason"><Textarea value={advForm.reason} onChange={(e) => setAdvForm({ ...advForm, reason: e.target.value })} /></Field>
                </div>
                <DialogFooter><Button variant="outline" onClick={() => setOpenAdv(false)}>Cancel</Button><Button className="btn-brand" onClick={applyAdv} data-testid="adv-submit">Submit</Button></DialogFooter>
              </DialogContent>
            </Dialog>
          )}
          <Card className="p-0 overflow-hidden">
            <Table>
              <TableHeader><TableRow><TableHead>Applicant</TableHead><TableHead>Amount</TableHead><TableHead>Status</TableHead><TableHead>Recovered</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
              <TableBody>
                {advs.map((a) => (
                  <TableRow key={a.id}>
                    <TableCell>{a.applicant_name}</TableCell>
                    <TableCell className="font-semibold">{fmtINR(a.amount)}</TableCell>
                    <TableCell><Badge variant="outline" className="capitalize">{a.status}</Badge></TableCell>
                    <TableCell>{a.recovered ? "Yes" : "—"}</TableCell>
                    <TableCell className="text-right space-x-1">
                      {a.status === "pending" && (user.role === a.chain?.[a.current_step] || (a.chain?.[a.current_step] === "department_manager" && user.is_department_manager)) && <>
                        <Button size="sm" className="btn-brand" onClick={() => actAdv(a, "approve")}>Approve</Button>
                        <Button size="sm" variant="outline" className="text-red-600" onClick={() => actAdv(a, "reject")}>Reject</Button>
                      </>}
                    </TableCell>
                  </TableRow>
                ))}
                {advs.length === 0 && <TableRow><TableCell colSpan={5} className="text-center py-10 text-sm opacity-60">No advances.</TableCell></TableRow>}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

// ============== FNF ==============
function FNFView() {
  const [items, setItems] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [open, setOpen] = useState(false);
  const empty = { employee_id: "", last_working_day: new Date().toISOString().slice(0, 10), leave_balance_days: 0, notice_period_recovery: 0, other_recovery: 0, other_payable: 0, notes: "" };
  const [form, setForm] = useState(empty);

  const load = () => Payroll.listFNF().then(setItems);
  useEffect(() => { load(); api.get("/employees/lookup/all").then((r) => setEmployees(r.data)); }, []);

  const save = async () => {
    if (!form.employee_id) return toast.error("Pick employee");
    try {
      const r = await Payroll.createFNF({
        ...form,
        leave_balance_days: Number(form.leave_balance_days),
        notice_period_recovery: Number(form.notice_period_recovery),
        other_recovery: Number(form.other_recovery),
        other_payable: Number(form.other_payable),
      });
      toast.success(`FNF computed — Net ${fmtINR(r.net_settlement)}`);
      setOpen(false); setForm(empty); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div>
      <PageHeader title="Full & Final Settlement" description="Compute FNF including gratuity and recoveries when an employee exits." action={
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button className="btn-brand" data-testid="fnf-new"><Plus size={16} className="mr-1.5" />New FNF</Button></DialogTrigger>
          <DialogContent className="max-w-xl">
            <DialogHeader><DialogTitle>New FNF Computation</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2"><Field label="Employee">
                <Select value={form.employee_id} onValueChange={(v) => setForm({ ...form, employee_id: v })}>
                  <SelectTrigger data-testid="fnf-employee"><SelectValue placeholder="Pick employee" /></SelectTrigger>
                  <SelectContent>{employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.full_name}</SelectItem>)}</SelectContent>
                </Select>
              </Field></div>
              <Field label="Last Working Day"><Input type="date" value={form.last_working_day} onChange={(e) => setForm({ ...form, last_working_day: e.target.value })} /></Field>
              <Field label="Leave Balance (days)"><Input type="number" value={form.leave_balance_days} onChange={(e) => setForm({ ...form, leave_balance_days: e.target.value })} /></Field>
              <Field label="Notice Recovery (₹)"><Input type="number" value={form.notice_period_recovery} onChange={(e) => setForm({ ...form, notice_period_recovery: e.target.value })} /></Field>
              <Field label="Other Recovery (₹)"><Input type="number" value={form.other_recovery} onChange={(e) => setForm({ ...form, other_recovery: e.target.value })} /></Field>
              <Field label="Other Payable (₹)"><Input type="number" value={form.other_payable} onChange={(e) => setForm({ ...form, other_payable: e.target.value })} /></Field>
              <div className="col-span-2"><Field label="Notes"><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></Field></div>
            </div>
            <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button className="btn-brand" onClick={save} data-testid="fnf-save">Compute</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      } />
      <Card className="p-0 overflow-hidden">
        <Table>
          <TableHeader><TableRow><TableHead>Employee</TableHead><TableHead>Last Day</TableHead><TableHead>Years</TableHead><TableHead>Payable</TableHead><TableHead>Recovery</TableHead><TableHead>Net</TableHead></TableRow></TableHeader>
          <TableBody>
            {items.map((f) => (
              <TableRow key={f.id}>
                <TableCell>{f.employee_name}</TableCell>
                <TableCell>{fmtDate(f.last_working_day)}</TableCell>
                <TableCell>{f.years_of_service}</TableCell>
                <TableCell>{fmtINR(f.total_payable)}</TableCell>
                <TableCell>{fmtINR(f.total_recovery)}</TableCell>
                <TableCell className={`font-semibold ${f.net_settlement < 0 ? "text-red-600" : "text-green-700"}`}>{fmtINR(f.net_settlement)}</TableCell>
              </TableRow>
            ))}
            {items.length === 0 && <TableRow><TableCell colSpan={6} className="text-center py-10 text-sm opacity-60">No FNF records.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}

// ============== REPORTS ==============
function ReportsView() {
  const reports = [
    { type: "salary", label: "Salary Register" },
    { type: "pf", label: "PF Register" },
    { type: "esi", label: "ESI Register" },
    { type: "tds", label: "TDS Register" },
    { type: "loans", label: "Loan Report" },
    { type: "advances", label: "Salary Advance Report" },
  ];
  return (
    <div>
      <PageHeader title="Payroll Reports" description="Generate Excel reports for statutory filings, audits and finance." />
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {reports.map((r) => (
          <Card key={r.type} className="p-5 flex items-center justify-between">
            <div>
              <div className="font-semibold">{r.label}</div>
              <div className="text-xs opacity-60 mt-1">Based on latest processed run</div>
            </div>
            <a href={Payroll.reportUrl(r.type)}>
              <Button size="sm" className="btn-brand" data-testid={`report-${r.type}`}><Download size={14} className="mr-1.5" />Excel</Button>
            </a>
          </Card>
        ))}
      </div>
    </div>
  );
}

// ============== SETTINGS ==============
function SettingsView() {
  return (
    <div>
      <PageHeader title="Payroll Settings" description="Statutory rates and rules — fully configurable, no code changes required for government updates." />
      <Tabs defaultValue="components">
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="components">Components</TabsTrigger>
          <TabsTrigger value="pf">PF</TabsTrigger>
          <TabsTrigger value="esi">ESI</TabsTrigger>
          <TabsTrigger value="pt">Prof. Tax</TabsTrigger>
          <TabsTrigger value="tds">TDS</TabsTrigger>
          <TabsTrigger value="bonus">Bonus</TabsTrigger>
          <TabsTrigger value="gratuity">Gratuity</TabsTrigger>
          <TabsTrigger value="cycle">Cycle</TabsTrigger>
          <TabsTrigger value="rounding">Rounding</TabsTrigger>
        </TabsList>
        <TabsContent value="components" className="mt-4"><ComponentsSettings /></TabsContent>
        <TabsContent value="pf" className="mt-4"><PFSettings /></TabsContent>
        <TabsContent value="esi" className="mt-4"><ESISettings /></TabsContent>
        <TabsContent value="pt" className="mt-4"><PTSettings /></TabsContent>
        <TabsContent value="tds" className="mt-4"><TDSSettings /></TabsContent>
        <TabsContent value="bonus" className="mt-4"><BonusSettings /></TabsContent>
        <TabsContent value="gratuity" className="mt-4"><GratuitySettings /></TabsContent>
        <TabsContent value="cycle" className="mt-4"><CycleSettings /></TabsContent>
        <TabsContent value="rounding" className="mt-4"><RoundingSettings /></TabsContent>
      </Tabs>
    </div>
  );
}

function SimpleConfigCard({ title, getter, putter, fields, testid }) {
  const [cfg, setCfg] = useState(null);
  useEffect(() => { getter().then(setCfg); }, []); // eslint-disable-line
  if (!cfg) return null;
  const save = async () => { try { await putter(cfg); toast.success("Saved"); } catch (e) { toast.error("Failed"); } };
  return (
    <Card className="p-5 max-w-2xl">
      <h3 className="font-semibold mb-3">{title}</h3>
      <div className="grid sm:grid-cols-2 gap-4">
        {fields.map((f) => (
          <div key={f.key}>
            {f.type === "switch" ? (
              <div className="flex items-center gap-2"><Switch checked={!!cfg[f.key]} onCheckedChange={(v) => setCfg({ ...cfg, [f.key]: v })} /><Label>{f.label}</Label></div>
            ) : (
              <Field label={f.label}><Input type={f.type || "number"} value={cfg[f.key] ?? ""} onChange={(e) => setCfg({ ...cfg, [f.key]: f.type === "text" ? e.target.value : Number(e.target.value) })} /></Field>
            )}
          </div>
        ))}
      </div>
      <div className="mt-4 text-right"><Button className="btn-brand" onClick={save} data-testid={testid}>Save</Button></div>
    </Card>
  );
}

const PFSettings = () => <SimpleConfigCard title="Provident Fund" getter={Payroll.getPF} putter={Payroll.putPF} testid="pf-save" fields={[
  { key: "enabled", label: "Enabled", type: "switch" },
  { key: "wage_ceiling", label: "Wage Ceiling (₹)" },
  { key: "employee_pf_pct", label: "Employee PF %" },
  { key: "employer_pf_pct", label: "Employer PF %" },
  { key: "epf_pct", label: "EPF %" },
  { key: "eps_pct", label: "EPS %" },
]} />;

const ESISettings = () => <SimpleConfigCard title="Employee State Insurance" getter={Payroll.getESI} putter={Payroll.putESI} testid="esi-save" fields={[
  { key: "enabled", label: "Enabled", type: "switch" },
  { key: "wage_ceiling", label: "Wage Ceiling (₹)" },
  { key: "employee_esi_pct", label: "Employee ESI %" },
  { key: "employer_esi_pct", label: "Employer ESI %" },
]} />;

const BonusSettings = () => <SimpleConfigCard title="Bonus" getter={Payroll.getBonus} putter={Payroll.putBonus} testid="bonus-save" fields={[
  { key: "enabled", label: "Enabled", type: "switch" },
  { key: "bonus_pct", label: "Bonus %" },
  { key: "eligibility_min_basic", label: "Min Basic for Eligibility (₹)" },
]} />;

const GratuitySettings = () => <SimpleConfigCard title="Gratuity" getter={Payroll.getGratuity} putter={Payroll.putGratuity} testid="gratuity-save" fields={[
  { key: "enabled", label: "Enabled", type: "switch" },
  { key: "min_years", label: "Min Years of Service" },
  { key: "days_per_year", label: "Days per Year" },
  { key: "divisor", label: "Divisor" },
]} />;

const CycleSettings = () => <SimpleConfigCard title="Payroll Cycle" getter={Payroll.getCycle} putter={Payroll.putCycle} testid="cycle-save" fields={[
  { key: "start_day", label: "Cycle Start Day" },
  { key: "end_day", label: "Cycle End Day" },
  { key: "pay_day", label: "Pay Day (next month)" },
]} />;

const RoundingSettings = () => {
  const [cfg, setCfg] = useState(null);
  useEffect(() => { Payroll.getRounding().then(setCfg); }, []);
  if (!cfg) return null;
  const save = async () => { try { await Payroll.putRounding(cfg); toast.success("Saved"); } catch (e) { toast.error("Failed"); } };
  return (
    <Card className="p-5 max-w-2xl">
      <h3 className="font-semibold mb-3">Salary Rounding Rules</h3>
      <div className="grid sm:grid-cols-2 gap-4">
        {["earnings_round","deductions_round","net_round"].map((k) => (
          <Field key={k} label={k.replace(/_/g," ")}>
            <Select value={cfg[k]} onValueChange={(v) => setCfg({ ...cfg, [k]: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="nearest">Nearest</SelectItem><SelectItem value="up">Up</SelectItem><SelectItem value="down">Down</SelectItem></SelectContent>
            </Select>
          </Field>
        ))}
        <Field label="Precision (decimals)"><Input type="number" value={cfg.precision} onChange={(e) => setCfg({ ...cfg, precision: Number(e.target.value) })} /></Field>
      </div>
      <div className="mt-4 text-right"><Button className="btn-brand" onClick={save} data-testid="rounding-save">Save</Button></div>
    </Card>
  );
};

function ComponentsSettings() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", code: "", type: "earning", category: "", is_taxable: true, is_statutory: false, active: true });
  const load = () => Payroll.listComponents().then(setItems);
  useEffect(() => { load(); }, []);
  const save = async () => {
    if (!form.name || !form.code) return toast.error("Name and code required");
    try { await Payroll.createComponent(form); toast.success("Saved"); setOpen(false); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  return (
    <Card className="p-5">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-semibold">Salary Components ({items.length})</h3>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button size="sm" className="btn-brand"><Plus size={14} className="mr-1" />Add</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>New Component</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Name"><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Field>
              <Field label="Code"><Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} /></Field>
              <Field label="Type">
                <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="earning">Earning</SelectItem><SelectItem value="deduction">Deduction</SelectItem></SelectContent>
                </Select>
              </Field>
              <Field label="Category"><Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} /></Field>
              <div className="flex items-center gap-2"><Switch checked={form.is_taxable} onCheckedChange={(v) => setForm({ ...form, is_taxable: v })} /><Label>Taxable</Label></div>
              <div className="flex items-center gap-2"><Switch checked={form.is_statutory} onCheckedChange={(v) => setForm({ ...form, is_statutory: v })} /><Label>Statutory</Label></div>
            </div>
            <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={save} className="btn-brand">Save</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
      <Table>
        <TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Code</TableHead><TableHead>Type</TableHead><TableHead>Category</TableHead><TableHead>Statutory</TableHead></TableRow></TableHeader>
        <TableBody>
          {items.map((c) => (
            <TableRow key={c.id}>
              <TableCell>{c.name}</TableCell>
              <TableCell className="font-mono text-xs">{c.code}</TableCell>
              <TableCell><Badge variant="outline" className="capitalize">{c.type}</Badge></TableCell>
              <TableCell>{c.category}</TableCell>
              <TableCell>{c.is_statutory ? "Yes" : "—"}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Card>
  );
}

function PTSettings() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ state: "", enabled: true, brackets: [{ min_salary: 0, max_salary: 0, monthly_amount: 0 }] });
  const load = () => Payroll.listPT().then(setItems);
  useEffect(() => { load(); }, []);
  const save = async () => {
    if (!form.state) return toast.error("State required");
    try { await Payroll.putPT(form.state, form); toast.success("Saved"); setOpen(false); load(); }
    catch (e) { toast.error("Failed"); }
  };
  return (
    <Card className="p-5">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-semibold">Professional Tax by State ({items.length})</h3>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button size="sm" className="btn-brand" data-testid="pt-new"><Plus size={14} className="mr-1" />Add State</Button></DialogTrigger>
          <DialogContent className="max-w-xl">
            <DialogHeader><DialogTitle>PT Configuration</DialogTitle></DialogHeader>
            <Field label="State"><Input value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} /></Field>
            <div className="mt-3 flex items-center justify-between"><Label>Brackets</Label>
              <Button size="sm" variant="outline" onClick={() => setForm({ ...form, brackets: [...form.brackets, { min_salary: 0, max_salary: 0, monthly_amount: 0 }] })}><Plus size={14} /></Button></div>
            <div className="space-y-2 mt-2">
              {form.brackets.map((b, i) => (
                <div key={i} className="grid grid-cols-3 gap-2">
                  <Input type="number" placeholder="Min" value={b.min_salary} onChange={(e) => { const a = [...form.brackets]; a[i] = { ...a[i], min_salary: Number(e.target.value) }; setForm({ ...form, brackets: a }); }} />
                  <Input type="number" placeholder="Max (0=∞)" value={b.max_salary} onChange={(e) => { const a = [...form.brackets]; a[i] = { ...a[i], max_salary: Number(e.target.value) }; setForm({ ...form, brackets: a }); }} />
                  <Input type="number" placeholder="₹/month" value={b.monthly_amount} onChange={(e) => { const a = [...form.brackets]; a[i] = { ...a[i], monthly_amount: Number(e.target.value) }; setForm({ ...form, brackets: a }); }} />
                </div>
              ))}
            </div>
            <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button className="btn-brand" onClick={save}>Save</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
      <Table>
        <TableHeader><TableRow><TableHead>State</TableHead><TableHead>Brackets</TableHead><TableHead>Enabled</TableHead></TableRow></TableHeader>
        <TableBody>{items.map((s) => (<TableRow key={s.id}><TableCell>{s.state}</TableCell><TableCell className="text-xs">{(s.brackets || []).length}</TableCell><TableCell>{s.enabled ? "Yes" : "No"}</TableCell></TableRow>))}</TableBody>
      </Table>
    </Card>
  );
}

function TDSSettings() {
  const [items, setItems] = useState([]);
  useEffect(() => { Payroll.listTDS().then(setItems); }, []);
  return (
    <Card className="p-5">
      <h3 className="font-semibold mb-3">TDS Configuration</h3>
      <Table>
        <TableHeader><TableRow><TableHead>Regime</TableHead><TableHead>FY</TableHead><TableHead>Std Deduction</TableHead><TableHead>Cess %</TableHead><TableHead>Slabs</TableHead></TableRow></TableHeader>
        <TableBody>{items.map((t) => (
          <TableRow key={t.id}>
            <TableCell className="capitalize">{t.regime}</TableCell>
            <TableCell>{t.financial_year}</TableCell>
            <TableCell>{fmtINR(t.standard_deduction)}</TableCell>
            <TableCell>{t.cess_pct}%</TableCell>
            <TableCell className="text-xs">{(t.slabs || []).map((s) => `${(s.min_income/1e5).toFixed(1)}L+${s.rate_pct}%`).join(" | ")}</TableCell>
          </TableRow>
        ))}</TableBody>
      </Table>
      <p className="text-xs opacity-60 mt-3">Default slabs are seeded for FY 2025-26 (Old + New). To edit, use PUT /api/v1/payroll/settings/tds/&lt;regime&gt;/&lt;fy&gt;.</p>
    </Card>
  );
}
